import React, { useEffect, useState, createContext, useContext } from 'react';
// Define types
export type EvaluationAttribute = {
  id: string;
  name: string;
  category: string;
  score: number;
};
export type EvaluationResult = {
  id: string;
  sampleNumber: string;
  date: string;
  time: string;
  attributes: EvaluationAttribute[];
  totalScore: number;
  classification: string;
  createdAt: Date;
};
type BreadEvaluationContextType = {
  currentEvaluation: EvaluationResult | null;
  evaluations: EvaluationResult[];
  initializeEvaluation: (sampleNumber: string, date: string, time: string) => void;
  updateScore: (attributeId: string, score: number) => void;
  finalizeEvaluation: () => void;
  getClassification: (score: number) => string;
  deleteEvaluation: (id: string) => void;
};
// Create context
const BreadEvaluationContext = createContext<BreadEvaluationContextType | undefined>(undefined);
// Default attributes based on ABNT NBR 16170
const defaultAttributes: EvaluationAttribute[] = [
// External characteristics (Crust)
{
  id: 'crustColor',
  name: 'Cor da crosta',
  category: 'Características externas (Crosta)',
  score: 0
}, {
  id: 'crustPestana',
  name: 'Pestana',
  category: 'Características externas (Crosta)',
  score: 0
}, {
  id: 'crustCrispness',
  name: 'Crocância',
  category: 'Características externas (Crosta)',
  score: 0
}, {
  id: 'crustAppearance',
  name: 'Aspecto',
  category: 'Características externas (Crosta)',
  score: 0
},
// External characteristics (Appearance)
{
  id: 'integrity',
  name: 'Integridade',
  category: 'Características externas (Aparência)',
  score: 0
}, {
  id: 'symmetry',
  name: 'Simetria',
  category: 'Características externas (Aparência)',
  score: 0
},
// Internal characteristics (Crumb)
{
  id: 'crumbAppearance',
  name: 'Aspecto da crosta',
  category: 'Características internas (Miolo)',
  score: 0
}, {
  id: 'crumbColor',
  name: 'Cor',
  category: 'Características internas (Miolo)',
  score: 0
}, {
  id: 'crumbTexture',
  name: 'Textura',
  category: 'Características internas (Miolo)',
  score: 0
}, {
  id: 'crumbCellStructure',
  name: 'Estrutura de células',
  category: 'Características internas (Miolo)',
  score: 0
},
// Sensory characteristics
{
  id: 'aroma',
  name: 'Aroma',
  category: 'Características sensoriais',
  score: 0
}, {
  id: 'flavor',
  name: 'Sabor',
  category: 'Características sensoriais',
  score: 0
}, {
  id: 'resilience',
  name: 'Resiliência',
  category: 'Características sensoriais',
  score: 0
}];
export const BreadEvaluationProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currentEvaluation, setCurrentEvaluation] = useState<EvaluationResult | null>(null);
  const [evaluations, setEvaluations] = useState<EvaluationResult[]>([]);
  // Load evaluations from localStorage on component mount
  useEffect(() => {
    const savedEvaluations = localStorage.getItem('breadEvaluations');
    if (savedEvaluations) {
      try {
        const parsed = JSON.parse(savedEvaluations);
        // Convert string dates back to Date objects
        const evaluationsWithDates = parsed.map((evaluation: any) => ({
          ...evaluation,
          createdAt: new Date(evaluation.createdAt)
        }));
        setEvaluations(evaluationsWithDates);
      } catch (error) {
        console.error('Error parsing saved evaluations:', error);
      }
    }
  }, []);
  // Save evaluations to localStorage whenever they change
  useEffect(() => {
    if (evaluations.length > 0) {
      localStorage.setItem('breadEvaluations', JSON.stringify(evaluations));
    }
  }, [evaluations]);
  const initializeEvaluation = (sampleNumber: string, date: string, time: string) => {
    setCurrentEvaluation({
      id: Date.now().toString(),
      sampleNumber,
      date,
      time,
      attributes: [...defaultAttributes],
      totalScore: 0,
      classification: '',
      createdAt: new Date()
    });
  };
  const updateScore = (attributeId: string, score: number) => {
    if (!currentEvaluation) return;
    const updatedAttributes = currentEvaluation.attributes.map(attr => attr.id === attributeId ? {
      ...attr,
      score
    } : attr);
    const totalScore = updatedAttributes.reduce((sum, attr) => sum + attr.score, 0);
    const classification = getClassification(totalScore);
    setCurrentEvaluation({
      ...currentEvaluation,
      attributes: updatedAttributes,
      totalScore,
      classification
    });
  };
  const finalizeEvaluation = () => {
    if (!currentEvaluation) return;
    setEvaluations(prev => [currentEvaluation, ...prev]);
    setCurrentEvaluation(null);
  };
  const getClassification = (score: number): string => {
    if (score >= 130) return 'Excelente qualidade (Conforme norma)';
    if (score >= 100) return 'Boa qualidade, com pequenos ajustes';
    if (score >= 70) return 'Qualidade regular, necessita melhorias';
    return 'Não conforme à norma';
  };
  const deleteEvaluation = (id: string) => {
    setEvaluations(prev => prev.filter(evaluation => evaluation.id !== id));
  };
  return <BreadEvaluationContext.Provider value={{
    currentEvaluation,
    evaluations,
    initializeEvaluation,
    updateScore,
    finalizeEvaluation,
    getClassification,
    deleteEvaluation
  }} data-id="element-52">
      {children}
    </BreadEvaluationContext.Provider>;
};
export const useBreadEvaluation = () => {
  const context = useContext(BreadEvaluationContext);
  if (context === undefined) {
    throw new Error('useBreadEvaluation must be used within a BreadEvaluationProvider');
  }
  return context;
};